import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtDate, fmtMoney, formatApiErrorDetail } from "@/lib/apiClient";
import { devLog } from "@/lib/devLog";
import { PageHeader, EmptyState, BcRibbonGroup, BcRibbonBtn } from "@/components/Shared";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  ArrowsClockwise, Plus, Trash, PencilSimple, Sparkle, Package, Warning, CaretRight,
  Cube, QrCode, MagnifyingGlass, TreeStructure, ChartLineUp, Path, Copy,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function KPI({ label, value, tone = "slate", testid }) {
  const cls = {
    slate: "bg-white border-slate-200 text-slate-900",
    blue: "bg-[#2E75B6] text-white border-[#2E75B6]",
    orange: "bg-[#EA580C] text-white border-[#EA580C]",
    dark: "bg-[#0F172A] text-white border-[#0F172A]",
    red: "bg-red-600 text-white border-red-600",
  }[tone];
  return (
    <div className={`border ${cls} rounded-sm p-4`} data-testid={testid}>
      <div className={`text-[10px] uppercase tracking-[0.14em] font-semibold ${tone === "slate" ? "text-slate-500" : "text-white/70"}`}>{label}</div>
      <div className="font-display text-[22px] font-bold font-mono-num mt-1">{value}</div>
    </div>
  );
}

export default function MaterialsIntelligence() {
  const [stats, setStats] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/materials/stats");
      setStats(data);
    } catch (e) { devLog.warn("materials/stats load failed", e); }
  }, []);
  useEffect(() => { load(); }, [load, refreshTick]);
  const notifyChange = useCallback(() => setRefreshTick((t) => t + 1), []);

  return (
    <div data-testid="materials-intel-page">
      <PageHeader
        title="Materials Intelligence"
        module="Procurement · Advanced"
        subtitle="Ten intelligence layers on top of your inventory — reorder automation, BOM library, AI demand forecast, batch tracking, QR scanning, cross-warehouse optimiser, anomaly detection, and material genealogy."
        ribbon={
          <BcRibbonGroup label="Process">
            <BcRibbonBtn icon={ArrowsClockwise} label="Refresh" onClick={load} testid="mi-refresh" />
          </BcRibbonGroup>
        }
      />
      <div className="p-6 space-y-5">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KPI label="BOMs" tone="dark" value={stats.boms} testid="mi-kpi-boms" />
            <KPI label="Reorder Rules" tone="slate" value={stats.active_reorder_rules} testid="mi-kpi-rules" />
            <KPI label="Live Batches" tone="blue" value={stats.active_batches} testid="mi-kpi-batches" />
            <KPI label="Bin Locations" tone="slate" value={stats.bins} testid="mi-kpi-bins" />
            <KPI label="Critical Stockouts" tone={stats.critical_reorder_breaches > 0 ? "red" : "slate"} value={stats.critical_reorder_breaches} testid="mi-kpi-crit" />
          </div>
        )}

        <Tabs defaultValue="reorder" className="w-full" data-testid="mi-tabs">
          <TabsList className="flex flex-wrap h-auto">
            <TabsTrigger value="reorder" data-testid="tab-reorder">Reorder Automation</TabsTrigger>
            <TabsTrigger value="bom" data-testid="tab-bom">BOM Library</TabsTrigger>
            <TabsTrigger value="batch" data-testid="tab-batch">Batch / Lot</TabsTrigger>
            <TabsTrigger value="forecast" data-testid="tab-forecast">AI Forecast</TabsTrigger>
            <TabsTrigger value="genealogy" data-testid="tab-genealogy">Genealogy</TabsTrigger>
            <TabsTrigger value="optimiser" data-testid="tab-optimiser">Transfer Optimiser</TabsTrigger>
            <TabsTrigger value="anomaly" data-testid="tab-anomaly">Anomalies</TabsTrigger>
            <TabsTrigger value="bins" data-testid="tab-bins">Bin Locations</TabsTrigger>
            <TabsTrigger value="qr" data-testid="tab-qr">QR / Barcode</TabsTrigger>
          </TabsList>

          <TabsContent value="reorder" className="mt-5"><ReorderPanel onChange={notifyChange} /></TabsContent>
          <TabsContent value="bom" className="mt-5"><BOMPanel onChange={notifyChange} /></TabsContent>
          <TabsContent value="batch" className="mt-5"><BatchPanel onChange={notifyChange} /></TabsContent>
          <TabsContent value="forecast" className="mt-5"><ForecastPanel /></TabsContent>
          <TabsContent value="genealogy" className="mt-5"><GenealogyPanel /></TabsContent>
          <TabsContent value="optimiser" className="mt-5"><OptimiserPanel /></TabsContent>
          <TabsContent value="anomaly" className="mt-5"><AnomalyPanel /></TabsContent>
          <TabsContent value="bins" className="mt-5"><BinsPanel onChange={notifyChange} /></TabsContent>
          <TabsContent value="qr" className="mt-5"><QRPanel /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// =============================================================================
// Panel: Reorder Automation
// =============================================================================
function ReorderPanel({ onChange }) {
  const [rules, setRules] = useState([]);
  const [scan, setScan] = useState(null);
  const [selected, setSelected] = useState({});
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([
      api.get("/materials/reorder-rules"),
      api.get("/materials/reorder-scan"),
    ]);
    setRules(a.data);
    setScan(b.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (r) => {
    if (!window.confirm(`Delete reorder rule for ${r.name}?`)) return;
    try { await api.delete(`/materials/reorder-rules/${r.id}`); toast.success("Rule deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const generatePOs = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length === 0) return toast.error("Select at least one item");
    try {
      const { data } = await api.post("/materials/reorder/auto-po", { rule_ids: ids, group_by_vendor: true });
      toast.success(`Created ${data.created} draft PO(s) — grouped by vendor.`);
      setSelected({});
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const totalBreaches = scan?.items?.length || 0;
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Button size="sm" className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white h-9" onClick={() => setCreating(true)} data-testid="reorder-new-rule">
          <Plus size={14} className="mr-1" /> New Reorder Rule
        </Button>
        <Button size="sm" variant="outline" className="rounded-sm h-9" onClick={load}><ArrowsClockwise size={14} className="mr-1" /> Refresh scan</Button>
        {totalBreaches > 0 && (
          <Button size="sm" className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white h-9 ml-auto" onClick={generatePOs} data-testid="reorder-auto-po">
            <Sparkle size={14} className="mr-1" weight="fill" /> Auto-generate PO(s) from selected
          </Button>
        )}
      </div>

      {/* Reorder scan */}
      <div className="bc-card border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">
            Stock breaches — {totalBreaches} rows ({scan?.critical || 0} critical)
          </div>
          <div className="text-[11px] text-slate-500">Below reorder point · one-click POs</div>
        </div>
        {(!scan || scan.items.length === 0) ? (
          <div className="p-6 text-center text-sm text-slate-400">🎉 All rules healthy — nothing below reorder point.</div>
        ) : (
          <Table className="bc-list">
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Material</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead className="text-right">Stock</TableHead>
                <TableHead className="text-right">ROP</TableHead>
                <TableHead className="text-right">Safety</TableHead>
                <TableHead className="text-right">Recommend</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Urgency</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {scan.items.map((b) => (
                <TableRow key={b.rule_id} data-testid={`reorder-scan-${b.rule_id}`}>
                  <TableCell><input type="checkbox" checked={!!selected[b.rule_id]} onChange={(e) => setSelected((s) => ({ ...s, [b.rule_id]: e.target.checked }))} data-testid={`reorder-select-${b.rule_id}`} /></TableCell>
                  <TableCell className="font-semibold text-slate-900">{b.name}</TableCell>
                  <TableCell className="font-mono text-[11.5px]">{b.sku}</TableCell>
                  <TableCell className={`text-right font-mono-num ${b.urgency === "critical" ? "text-red-700 font-bold" : "text-slate-700"}`}>{b.current_stock}</TableCell>
                  <TableCell className="text-right font-mono-num text-slate-500">{b.reorder_point}</TableCell>
                  <TableCell className="text-right font-mono-num text-slate-500">{b.safety_stock}</TableCell>
                  <TableCell className="text-right font-mono-num font-semibold text-[#EA580C]">{b.recommended_qty} {b.unit}</TableCell>
                  <TableCell className="text-[12px] text-slate-600">{b.preferred_vendor || "—"}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center rounded-sm border px-1.5 py-[1px] text-[10px] font-semibold uppercase tracking-widest ${b.urgency === "critical" ? "bg-red-50 text-red-700 border-red-300" : "bg-amber-50 text-amber-800 border-amber-300"}`}>{b.urgency}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Rules master */}
      <div className="bc-card border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">All reorder rules · {rules.length}</div>
        </div>
        {rules.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-400">No rules yet. Add one to start automated procurement.</div>
        ) : (
          <Table className="bc-list">
            <TableHeader>
              <TableRow>
                <TableHead>Material</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead className="text-right">Reorder Pt</TableHead>
                <TableHead className="text-right">Safety</TableHead>
                <TableHead className="text-right">Target</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-semibold text-slate-900">{r.name}</TableCell>
                  <TableCell className="font-mono text-[11.5px]">{r.sku}</TableCell>
                  <TableCell className="text-right font-mono-num">{r.reorder_point}</TableCell>
                  <TableCell className="text-right font-mono-num text-slate-500">{r.safety_stock}</TableCell>
                  <TableCell className="text-right font-mono-num">{r.target_stock}</TableCell>
                  <TableCell className="text-[12px] text-slate-600">{r.preferred_vendor || "—"}</TableCell>
                  <TableCell className="text-[11px]">{r.is_active ? <span className="text-emerald-700 font-semibold uppercase tracking-widest">Active</span> : <span className="text-slate-400">Paused</span>}</TableCell>
                  <TableCell>
                    <button onClick={() => del(r)} className="text-slate-400 hover:text-red-600 p-1" title="Delete"><Trash size={13} /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {creating && <ReorderRuleSheet open={creating} onOpenChange={setCreating} onSaved={() => { setCreating(false); load(); onChange?.(); }} />}
    </div>
  );
}

function ReorderRuleSheet({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({
    sku: "", name: "", reorder_point: 0, safety_stock: 0, target_stock: 0,
    preferred_vendor: "", unit: "nos", last_unit_cost: 0, notes: "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Material name is required");
    if (!form.sku.trim()) return toast.error("SKU is required");
    if (Number(form.reorder_point) <= 0) return toast.error("Reorder point must be > 0");
    if (Number(form.target_stock) < Number(form.reorder_point)) return toast.error("Target stock must be ≥ reorder point");
    setSaving(true);
    try {
      await api.post("/materials/reorder-rules", {
        ...form,
        reorder_point: Number(form.reorder_point),
        safety_stock: Number(form.safety_stock),
        target_stock: Number(form.target_stock),
        last_unit_cost: Number(form.last_unit_cost),
      });
      toast.success("Reorder rule created");
      onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[500px] max-w-md overflow-y-auto">
        <SheetHeader><SheetTitle className="font-display">New Reorder Rule</SheetTitle></SheetHeader>
        <div className="mt-5 space-y-3">
          <Field label="Material name *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="rr-name" /></Field>
          <Field label="SKU *"><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="font-mono uppercase" data-testid="rr-sku" /></Field>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Reorder Point *"><Input type="number" value={form.reorder_point} onChange={(e) => setForm({ ...form, reorder_point: e.target.value })} data-testid="rr-rop" /></Field>
            <Field label="Safety Stock"><Input type="number" value={form.safety_stock} onChange={(e) => setForm({ ...form, safety_stock: e.target.value })} /></Field>
            <Field label="Target Stock *"><Input type="number" value={form.target_stock} onChange={(e) => setForm({ ...form, target_stock: e.target.value })} data-testid="rr-target" /></Field>
          </div>
          <Field label="Preferred Vendor"><Input value={form.preferred_vendor} onChange={(e) => setForm({ ...form, preferred_vendor: e.target.value })} data-testid="rr-vendor" /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Unit"><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="uppercase" /></Field>
            <Field label="Last Unit Cost (₹)"><Input type="number" value={form.last_unit_cost} onChange={(e) => setForm({ ...form, last_unit_cost: e.target.value })} /></Field>
          </div>
          <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} disabled={saving} className="flex-1 rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="rr-save">{saving ? "Saving…" : "Create Rule"}</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// =============================================================================
// Panel: BOM Library
// =============================================================================
function BOMPanel({ onChange }) {
  const [boms, setBoms] = useState([]);
  const [creating, setCreating] = useState(false);
  const [expanding, setExpanding] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/materials/boms");
    setBoms(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (b) => {
    if (!window.confirm(`Delete BOM "${b.name}"?`)) return;
    try { await api.delete(`/materials/boms/${b.id}`); toast.success("BOM deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">Bills of Materials · {boms.length}</div>
        <Button size="sm" className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white h-9" onClick={() => setCreating(true)} data-testid="bom-new">
          <Plus size={14} className="mr-1" /> New BOM
        </Button>
      </div>

      {boms.length === 0 ? (
        <EmptyState title="No BOMs yet" description="Create a Bill of Materials kit — a one-click template that expands into a Material Request." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {boms.map((b) => (
            <div key={b.id} className="border border-slate-200 rounded-sm bg-white p-4 hover:border-[#EA580C] transition-colors" data-testid={`bom-card-${b.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="text-[15px] font-semibold text-slate-900">{b.name}</div>
                  {b.category && <div className="text-[11px] uppercase tracking-widest text-slate-500 mt-0.5">{b.category}</div>}
                </div>
                <span className="text-[10px] font-mono-num text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded-sm">×{b.times_used}</span>
              </div>
              {b.description && <div className="text-[12px] text-slate-600 mb-3">{b.description}</div>}
              <div className="text-[11px] text-slate-500 border-t border-slate-100 pt-2 mb-3">
                {(b.items || []).length} items · {b.items?.slice(0, 3).map((i) => i.material_name).join(", ")}{b.items?.length > 3 ? "…" : ""}
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="rounded-sm flex-1 h-8 text-[12px]" onClick={() => setExpanding(b)} data-testid={`bom-expand-${b.id}`}>
                  <Cube size={12} className="mr-1" /> Expand to MR
                </Button>
                <button onClick={() => del(b)} className="text-slate-400 hover:text-red-600 p-2" title="Delete"><Trash size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && <BOMSheet open={creating} onOpenChange={setCreating} onSaved={() => { setCreating(false); load(); onChange?.(); }} />}
      {expanding && <BOMExpandDialog open={!!expanding} onOpenChange={(v) => !v && setExpanding(null)} bom={expanding} onDone={() => { setExpanding(null); load(); }} />}
    </div>
  );
}

function BOMSheet({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ name: "", category: "", description: "", items: [{ material_name: "", sku: "", qty_per_unit: 1, unit: "nos", hsn: "", gst_rate: 18, notes: "" }] });
  const [saving, setSaving] = useState(false);
  const patch = (i, k, v) => setForm((f) => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  const add = () => setForm((f) => ({ ...f, items: [...f.items, { material_name: "", sku: "", qty_per_unit: 1, unit: "nos", hsn: "", gst_rate: 18, notes: "" }] }));
  const remove = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("BOM name is required");
    const items = form.items.filter((it) => (it.material_name || "").trim());
    if (items.length === 0) return toast.error("At least one material is required");
    setSaving(true);
    try {
      await api.post("/materials/boms", { ...form, items: items.map((it) => ({ ...it, qty_per_unit: Number(it.qty_per_unit), gst_rate: Number(it.gst_rate) })) });
      toast.success("BOM created");
      onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[700px] sm:max-w-2xl lg:w-[min(880px,95vw)] lg:max-w-none overflow-y-auto">
        <SheetHeader><SheetTitle className="font-display">New BOM / Kit</SheetTitle></SheetHeader>
        <div className="mt-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="BOM Name *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="3-Ton VRF Install Kit" data-testid="bom-name" /></Field>
            <Field label="Category"><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Installation / PPM / Repair" /></Field>
          </div>
          <Field label="Description"><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <div className="border border-slate-200 rounded-sm">
            <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">Materials · {form.items.length}</div>
              <button onClick={add} className="text-[11px] font-semibold text-[#EA580C] hover:underline flex items-center gap-1" data-testid="bom-add-item"><Plus size={12} /> Add row</button>
            </div>
            <div className="p-3">
              <Table className="bc-list text-[12.5px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Material</TableHead>
                    <TableHead className="w-24">SKU</TableHead>
                    <TableHead className="w-20 text-right">Qty/Unit</TableHead>
                    <TableHead className="w-20">UoM</TableHead>
                    <TableHead className="w-20">HSN</TableHead>
                    <TableHead className="w-16 text-right">GST%</TableHead>
                    <TableHead className="w-6"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {form.items.map((it, i) => (
                    <TableRow key={`bom-line-${i}`}>
                      <TableCell><Input value={it.material_name} onChange={(e) => patch(i, "material_name", e.target.value)} className="h-7" data-testid={`bom-mat-${i}`} /></TableCell>
                      <TableCell><Input value={it.sku} onChange={(e) => patch(i, "sku", e.target.value)} className="h-7 font-mono uppercase" /></TableCell>
                      <TableCell><Input type="number" value={it.qty_per_unit} onChange={(e) => patch(i, "qty_per_unit", e.target.value)} className="h-7 text-right font-mono-num" data-testid={`bom-qty-${i}`} /></TableCell>
                      <TableCell><Input value={it.unit} onChange={(e) => patch(i, "unit", e.target.value)} className="h-7 uppercase" /></TableCell>
                      <TableCell><Input value={it.hsn} onChange={(e) => patch(i, "hsn", e.target.value)} className="h-7 font-mono" /></TableCell>
                      <TableCell><Input type="number" value={it.gst_rate} onChange={(e) => patch(i, "gst_rate", e.target.value)} className="h-7 text-right font-mono-num" /></TableCell>
                      <TableCell><button onClick={() => remove(i)} className="text-slate-400 hover:text-red-600"><Trash size={11} /></button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} disabled={saving} className="flex-1 rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="bom-save">{saving ? "Saving…" : "Create BOM"}</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function BOMExpandDialog({ open, onOpenChange, bom, onDone }) {
  const [multiplier, setMultiplier] = useState(1);
  const [expanded, setExpanded] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open && bom?.id) {
      api.post(`/materials/boms/${bom.id}/expand?multiplier=${multiplier}`).then((r) => setExpanded(r.data));
      api.get("/projects").then((r) => setProjects(r.data || []));
    }
  }, [open, bom?.id, multiplier]);

  const createMR = async () => {
    if (!projectId) return toast.error("Pick a project first");
    if (!expanded?.items?.length) return toast.error("Nothing to expand");
    setCreating(true);
    try {
      const items = expanded.items.map((it) => ({
        material_name: it.material_name,
        sku: it.sku || "",
        qty: it.qty,
        unit: it.unit,
        hsn: it.hsn || "",
        gst_rate: it.gst_rate || 18,
        notes: it.notes || "",
      }));
      await api.post("/material-requests", {
        project_id: projectId,
        priority: "medium",
        required_by: new Date(Date.now() + 5 * 86400000).toISOString().slice(0, 10),
        items,
        notes: `Auto-generated from BOM "${bom.name}" × ${multiplier}`,
      });
      toast.success(`Material Request created from ${bom.name} × ${multiplier}`);
      onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="bom-expand-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Expand · {bom?.name}</DialogTitle>
          <DialogDescription className="sr-only">Expand BOM into a Material Request with scaling multiplier.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label className="label-caps mb-1 block">Project *</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger data-testid="bom-expand-project"><SelectValue placeholder="Pick project" /></SelectTrigger>
                <SelectContent>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-caps mb-1 block">Multiplier</Label>
              <Input type="number" value={multiplier} min="0.1" step="0.5" onChange={(e) => setMultiplier(Number(e.target.value) || 1)} className="w-24 font-mono-num text-right" data-testid="bom-multiplier" />
            </div>
          </div>
          {expanded && (
            <Table className="bc-list">
              <TableHeader>
                <TableRow>
                  <TableHead>Material</TableHead>
                  <TableHead className="w-24">SKU</TableHead>
                  <TableHead className="w-20 text-right">Qty</TableHead>
                  <TableHead className="w-16">UoM</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expanded.items.map((it) => (
                  <TableRow key={it.sku || it.material_name}>
                    <TableCell>{it.material_name}</TableCell>
                    <TableCell className="font-mono text-[11.5px]">{it.sku || "—"}</TableCell>
                    <TableCell className="text-right font-mono-num font-semibold">{it.qty}</TableCell>
                    <TableCell className="text-[11px] uppercase">{it.unit}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <div className="flex gap-2 pt-2 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={createMR} disabled={creating || !expanded} className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="bom-create-mr">
              {creating ? "Creating…" : "Create Material Request →"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Panel: Batch / Lot
// =============================================================================
function BatchPanel({ onChange }) {
  const [batches, setBatches] = useState([]);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/materials/batches?only_active=true");
    setBatches(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">Active batches · {batches.length}</div>
        <Button size="sm" className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white h-9" onClick={() => setCreating(true)} data-testid="batch-new">
          <Plus size={14} className="mr-1" /> New Batch
        </Button>
      </div>
      {batches.length === 0 ? (
        <EmptyState title="No active batches" description="Record incoming batches with a lot number, manufacture and expiry date to trace material genealogy." />
      ) : (
        <div className="bc-card border border-slate-200 rounded-sm overflow-hidden">
          <Table className="bc-list">
            <TableHeader>
              <TableRow>
                <TableHead>Batch #</TableHead>
                <TableHead>Material</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead>Mfg</TableHead>
                <TableHead>Expiry</TableHead>
                <TableHead className="text-right">Received</TableHead>
                <TableHead className="text-right">Remaining</TableHead>
                <TableHead className="text-right">Unit Cost</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {batches.map((b) => {
                const nearExpiry = b.expiry_date && new Date(b.expiry_date) < new Date(Date.now() + 30 * 86400000);
                return (
                  <TableRow key={b.id} data-testid={`batch-row-${b.id}`}>
                    <TableCell className="font-mono text-[11.5px] font-semibold">{b.batch_number}</TableCell>
                    <TableCell>{b.name}</TableCell>
                    <TableCell className="font-mono text-[11.5px]">{b.sku}</TableCell>
                    <TableCell className="text-[12px]">{b.manufacture_date ? fmtDate(b.manufacture_date) : "—"}</TableCell>
                    <TableCell className={`text-[12px] ${nearExpiry ? "text-red-600 font-semibold" : ""}`}>{b.expiry_date ? fmtDate(b.expiry_date) : "—"}</TableCell>
                    <TableCell className="text-right font-mono-num">{b.qty_received}</TableCell>
                    <TableCell className="text-right font-mono-num font-semibold">{b.qty_remaining} {b.unit}</TableCell>
                    <TableCell className="text-right font-mono-num">{fmtMoney(b.unit_cost)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      {creating && <BatchSheet open={creating} onOpenChange={setCreating} onSaved={() => { setCreating(false); load(); onChange?.(); }} />}
    </div>
  );
}

function BatchSheet({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({
    sku: "", name: "", batch_number: "", manufacture_date: "", expiry_date: "",
    qty_received: 0, unit_cost: 0, unit: "nos", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.name || !form.sku || !form.batch_number || Number(form.qty_received) <= 0)
      return toast.error("Material, SKU, batch # and qty are required");
    setSaving(true);
    try {
      await api.post("/materials/batches", {
        ...form,
        qty_received: Number(form.qty_received),
        unit_cost: Number(form.unit_cost),
        manufacture_date: form.manufacture_date || null,
        expiry_date: form.expiry_date || null,
      });
      toast.success("Batch recorded");
      onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[500px] max-w-md overflow-y-auto">
        <SheetHeader><SheetTitle className="font-display">Record Batch / Lot</SheetTitle></SheetHeader>
        <div className="mt-5 space-y-3">
          <Field label="Material Name *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="batch-name" /></Field>
          <Field label="SKU *"><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="font-mono uppercase" data-testid="batch-sku" /></Field>
          <Field label="Batch # *"><Input value={form.batch_number} onChange={(e) => setForm({ ...form, batch_number: e.target.value })} className="font-mono uppercase" data-testid="batch-no" /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Mfg Date"><Input type="date" value={form.manufacture_date} onChange={(e) => setForm({ ...form, manufacture_date: e.target.value })} /></Field>
            <Field label="Expiry"><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Qty *"><Input type="number" value={form.qty_received} onChange={(e) => setForm({ ...form, qty_received: e.target.value })} data-testid="batch-qty" /></Field>
            <Field label="Unit"><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="uppercase" /></Field>
            <Field label="Unit Cost ₹"><Input type="number" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} /></Field>
          </div>
          <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} disabled={saving} className="flex-1 rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="batch-save">{saving ? "Saving…" : "Record Batch"}</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// =============================================================================
// Panel: AI Demand Forecast
// =============================================================================
function ForecastPanel() {
  const [form, setForm] = useState({ sku: "", name: "", horizon_days: 30 });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    if (!form.sku && !form.name) return toast.error("Provide SKU or material name");
    setBusy(true);
    try {
      const { data } = await api.post("/materials/forecast", { ...form, horizon_days: Number(form.horizon_days) });
      setResult(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-5">
      <div className="border border-slate-200 rounded-sm bg-white p-4">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 mb-3 flex items-center gap-2">
          <Sparkle size={12} className="text-[#EA580C]" weight="fill" /> Ask the AI · demand forecast
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="SKU"><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="font-mono uppercase" data-testid="fc-sku" /></Field>
          <Field label="or Material Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="fc-name" /></Field>
          <Field label="Horizon">
            <Select value={String(form.horizon_days)} onValueChange={(v) => setForm({ ...form, horizon_days: Number(v) })}>
              <SelectTrigger data-testid="fc-horizon"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="60">60 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <div className="flex items-end">
            <Button onClick={run} disabled={busy} className="w-full rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="fc-run">
              {busy ? "Forecasting…" : "Forecast →"}
            </Button>
          </div>
        </div>
      </div>
      {result && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI label={`Forecast · ${result.horizon_days}d`} tone="orange" value={result.recommended_forecast_qty ?? "—"} testid="fc-kpi-final" />
            <KPI label="Consumed (90d)" tone="dark" value={result.history.consumed_90d} />
            <KPI label="Received (90d)" tone="slate" value={result.history.received_90d} />
            <KPI label="Statistical Baseline" tone="blue" value={result.statistical_baseline ?? "—"} />
          </div>
          {result.llm && (
            <div className="border border-slate-200 rounded-sm bg-white p-4 space-y-3">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 flex items-center gap-2"><Sparkle size={12} className="text-[#EA580C]" weight="fill" /> AI reasoning</div>
              <div className="grid grid-cols-4 gap-2 text-[12px]">
                <div><span className="label-caps text-slate-500">Trend</span><div className="font-semibold uppercase">{result.llm.trend}</div></div>
                <div><span className="label-caps text-slate-500">Confidence</span><div className="font-semibold uppercase">{result.llm.confidence}</div></div>
                <div><span className="label-caps text-slate-500">P20</span><div className="font-mono-num font-semibold">{result.llm.lower_bound}</div></div>
                <div><span className="label-caps text-slate-500">P80</span><div className="font-mono-num font-semibold">{result.llm.upper_bound}</div></div>
              </div>
              {result.llm.recommended_reorder_point > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-sm p-2 text-[12.5px]">
                  <span className="font-semibold text-[#EA580C]">Recommended reorder point:</span> {result.llm.recommended_reorder_point} {result.llm.trend === "growing" ? "(consider raising)" : ""}
                </div>
              )}
              {result.reasoning && (
                <div className="text-[13px] text-slate-700 border-t border-slate-100 pt-2">{result.reasoning}</div>
              )}
            </div>
          )}
          {!result.llm && result.reasoning && (
            <div className="border border-slate-200 rounded-sm bg-amber-50 p-3 text-[12.5px] text-slate-700">{result.reasoning}</div>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Panel: Material Genealogy
// =============================================================================
function GenealogyPanel() {
  const [form, setForm] = useState({ sku: "", name: "" });
  const [tree, setTree] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!form.sku && !form.name) return toast.error("Provide SKU or material name");
    setBusy(true);
    try {
      const p = new URLSearchParams();
      if (form.sku) p.set("sku", form.sku);
      if (form.name) p.set("name", form.name);
      const { data } = await api.get(`/materials/genealogy?${p.toString()}`);
      setTree(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-5">
      <div className="border border-slate-200 rounded-sm bg-white p-4">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 mb-3 flex items-center gap-2">
          <TreeStructure size={12} className="text-[#EA580C]" /> Material Genealogy · full trace
        </div>
        <div className="flex items-end gap-3">
          <Field label="SKU"><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="font-mono uppercase" data-testid="gen-sku" /></Field>
          <Field label="or Material Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="gen-name" /></Field>
          <Button onClick={run} disabled={busy} className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="gen-run">Trace →</Button>
        </div>
      </div>
      {tree && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <KPI label="Purchases" tone="slate" value={tree.totals.purchases} />
          <KPI label="Goods Receipts" tone="blue" value={tree.totals.goods_receipts} />
          <KPI label="Batches" tone="orange" value={tree.totals.batches} />
          <KPI label="Material Requests" tone="dark" value={tree.totals.material_requests} />
        </div>
      )}
      {tree && (tree.purchases.length + tree.goods_receipts.length + tree.batches.length + tree.material_requests.length) > 0 && (
        <div className="space-y-3">
          {tree.purchases.length > 0 && (
            <TraceSection title={`Purchases (${tree.purchases.length})`} items={tree.purchases.map((p) => ({
              headline: `${p.invoice_number || "—"} · ${p.vendor}`,
              meta: `${fmtDate(p.date)} · ${p.lines.length} line(s)`,
              sub: p.lines.map((l) => `${l.description || l.name || l.sku} — ${l.qty} ${l.unit || ""} @ ₹${l.unit_price}`).join(" · "),
              key: p.purchase_id,
            }))} />
          )}
          {tree.goods_receipts.length > 0 && (
            <TraceSection title={`Goods Receipts (${tree.goods_receipts.length})`} items={tree.goods_receipts.map((g) => ({
              headline: g.grn_number || "GRN",
              meta: `${fmtDate(g.received_at)} · ${g.vendor}`,
              sub: g.lines.map((l) => `${l.description || l.name || l.sku} — ${l.qty || l.received_qty || 0}`).join(" · "),
              key: g.grn_id,
            }))} />
          )}
          {tree.batches.length > 0 && (
            <TraceSection title={`Batches (${tree.batches.length})`} items={tree.batches.map((b) => ({
              headline: `${b.batch_number} · ${b.qty_remaining}/${b.qty_received}`,
              meta: `${b.manufacture_date ? "mfg " + fmtDate(b.manufacture_date) : ""} ${b.expiry_date ? "· exp " + fmtDate(b.expiry_date) : ""}`.trim(),
              sub: `Cost ₹${b.unit_cost} · Consumed by ${(b.consumed_by || []).length} MR(s)`,
              key: b.id,
            }))} />
          )}
          {tree.material_requests.length > 0 && (
            <TraceSection title={`Material Requests (${tree.material_requests.length})`} items={tree.material_requests.map((m) => ({
              headline: `${m.mr_number || m.mr_id.slice(0, 8)} · ${m.status}`,
              meta: `${fmtDate(m.created_at)} · project ${m.project_id?.slice(0, 8) || "—"}`,
              sub: m.lines.map((l) => `${l.material_name || l.sku} — ${l.qty} ${l.unit || ""}`).join(" · "),
              key: m.mr_id,
            }))} />
          )}
        </div>
      )}
      {tree && (tree.purchases.length + tree.goods_receipts.length + tree.batches.length + tree.material_requests.length) === 0 && (
        <EmptyState title="No history found" description="This material hasn't shown up in any purchase, GRN, batch or MR yet." />
      )}
    </div>
  );
}

function TraceSection({ title, items }) {
  return (
    <div className="border border-slate-200 rounded-sm bg-white">
      <div className="px-3 py-2 border-b border-slate-200 bg-slate-50">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">{title}</div>
      </div>
      <div className="divide-y divide-slate-100">
        {items.map((it) => (
          <div key={it.key} className="p-3 hover:bg-slate-50">
            <div className="flex items-center gap-2">
              <CaretRight size={12} className="text-[#EA580C]" />
              <div className="font-semibold text-slate-900 text-[13px]">{it.headline}</div>
              <div className="text-[11px] text-slate-500 font-mono-num ml-auto">{it.meta}</div>
            </div>
            <div className="ml-4 text-[12px] text-slate-600 mt-1">{it.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// Panel: Cross-Warehouse Optimiser
// =============================================================================
function OptimiserPanel() {
  const [form, setForm] = useState({ sku: "", qty: 1 });
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!form.sku) return toast.error("SKU is required");
    setBusy(true);
    try {
      const p = new URLSearchParams({ sku: form.sku, qty: form.qty });
      const { data } = await api.get(`/materials/optimise-transfer?${p.toString()}`);
      setPlan(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <div className="border border-slate-200 rounded-sm bg-white p-4">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 mb-3 flex items-center gap-2">
          <Path size={12} className="text-[#EA580C]" /> Cross-warehouse optimiser
        </div>
        <div className="flex items-end gap-3">
          <Field label="SKU *"><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="font-mono uppercase" data-testid="opt-sku" /></Field>
          <Field label="Qty required *"><Input type="number" value={form.qty} onChange={(e) => setForm({ ...form, qty: Number(e.target.value) || 0 })} data-testid="opt-qty" /></Field>
          <Button onClick={run} disabled={busy} className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="opt-run">Plan transfer →</Button>
        </div>
      </div>
      {plan && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <KPI label="Requested Qty" tone="dark" value={plan.requested_qty} />
            <KPI label="Total Available" tone="blue" value={plan.total_available} />
            <KPI label="Shortfall" tone={plan.shortfall > 0 ? "red" : "slate"} value={plan.shortfall} />
          </div>
          {plan.candidates.length === 0 ? (
            <EmptyState title="No warehouse has this SKU" description="Add stock to a warehouse or check the SKU spelling." />
          ) : (
            <div className="bc-card border border-slate-200 rounded-sm overflow-hidden">
              <Table className="bc-list">
                <TableHeader>
                  <TableRow>
                    <TableHead>Warehouse</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead className="text-right">Available</TableHead>
                    <TableHead className="text-right">Take</TableHead>
                    <TableHead>Unit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plan.candidates.map((c) => (
                    <TableRow key={c.warehouse_id}>
                      <TableCell className="font-semibold">{c.warehouse_name}</TableCell>
                      <TableCell className="text-[12px] text-slate-500">{c.warehouse_city || "—"}</TableCell>
                      <TableCell className="text-right font-mono-num">{c.available}</TableCell>
                      <TableCell className="text-right font-mono-num font-semibold text-[#EA580C]">{c.take}</TableCell>
                      <TableCell className="text-[11px] uppercase">{c.unit}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// =============================================================================
// Panel: Anomaly Monitor
// =============================================================================
function AnomalyPanel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [z, setZ] = useState(2.0);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/materials/anomalies?days=${days}&z_threshold=${z}`);
      setData(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }, [days, z]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="border border-slate-200 rounded-sm bg-white p-4">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 mb-3 flex items-center gap-2">
          <ChartLineUp size={12} className="text-[#EA580C]" /> Consumption anomaly monitor
        </div>
        <div className="flex items-end gap-3">
          <Field label="Look-back (days)"><Input type="number" value={days} onChange={(e) => setDays(Number(e.target.value) || 30)} className="w-28 font-mono-num" data-testid="anom-days" /></Field>
          <Field label="z-threshold"><Input type="number" step="0.1" value={z} onChange={(e) => setZ(Number(e.target.value) || 2.0)} className="w-28 font-mono-num" /></Field>
          <Button onClick={load} className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="anom-run">Scan →</Button>
        </div>
      </div>
      {data && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <KPI label="Sample Size" tone="slate" value={data.sample_size} />
            <KPI label="Anomalies Found" tone={data.anomalies.length > 0 ? "red" : "slate"} value={data.anomalies.length} />
            <KPI label="Threshold" tone="dark" value={`z ≥ ${data.z_threshold}`} />
          </div>
          {data.anomalies.length === 0 ? (
            <EmptyState title="No anomalies detected" description="Every material's recent consumption is within its normal band." />
          ) : (
            <div className="bc-card border border-slate-200 rounded-sm overflow-hidden">
              <Table className="bc-list">
                <TableHeader>
                  <TableRow>
                    <TableHead>Material</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead className="text-right">Qty (event)</TableHead>
                    <TableHead className="text-right">Mean</TableHead>
                    <TableHead className="text-right">z-Score</TableHead>
                    <TableHead>Type</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.anomalies.map((a) => (
                    <TableRow key={`${a.sku}-${a.name}-${a.qty}-${a.z_score}`}>
                      <TableCell className="font-semibold">{a.name}</TableCell>
                      <TableCell className="font-mono text-[11.5px]">{a.sku || "—"}</TableCell>
                      <TableCell className="text-right font-mono-num">{a.qty} {a.unit}</TableCell>
                      <TableCell className="text-right font-mono-num text-slate-500">{a.mean}</TableCell>
                      <TableCell className={`text-right font-mono-num font-semibold ${a.z_score > 0 ? "text-red-700" : "text-blue-700"}`}>{a.z_score}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center rounded-sm border px-1.5 py-[1px] text-[10px] font-semibold uppercase tracking-widest ${a.type === "spike" ? "bg-red-50 text-red-700 border-red-300" : "bg-blue-50 text-blue-700 border-blue-300"}`}>
                          {a.type}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// =============================================================================
// Panel: Bin Locations
// =============================================================================
function BinsPanel({ onChange }) {
  const [bins, setBins] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (warehouseId) params.set("warehouse_id", warehouseId);
    const [a, b] = await Promise.all([api.get(`/materials/bins?${params.toString()}`), api.get("/warehouses")]);
    setBins(a.data);
    setWarehouses(b.data || []);
    if (!warehouseId && (b.data || []).length > 0) setWarehouseId(b.data[0].id);
  }, [warehouseId]);
  useEffect(() => { load(); }, [load]);

  const del = async (b) => {
    if (!window.confirm(`Delete bin ${b.code}?`)) return;
    try { await api.delete(`/materials/bins/${b.id}`); toast.success("Bin deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const whName = useMemo(() => Object.fromEntries(warehouses.map((w) => [w.id, w.name])), [warehouses]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Select value={warehouseId} onValueChange={setWarehouseId}>
          <SelectTrigger className="w-64" data-testid="bin-wh-filter"><SelectValue placeholder="All warehouses" /></SelectTrigger>
          <SelectContent>
            {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white h-9 ml-auto" onClick={() => setCreating(true)} data-testid="bin-new">
          <Plus size={14} className="mr-1" /> New Bin
        </Button>
      </div>
      {bins.length === 0 ? (
        <EmptyState title="No bins configured" description="Set up Zone → Rack → Shelf → Bin for pick-list generation." />
      ) : (
        <div className="bc-card border border-slate-200 rounded-sm overflow-hidden">
          <Table className="bc-list">
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Warehouse</TableHead>
                <TableHead>Zone</TableHead>
                <TableHead>Rack</TableHead>
                <TableHead>Shelf</TableHead>
                <TableHead>Bin</TableHead>
                <TableHead className="text-right">Capacity</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bins.map((b) => (
                <TableRow key={b.id} data-testid={`bin-row-${b.id}`}>
                  <TableCell className="font-mono text-[11.5px] font-semibold">{b.code}</TableCell>
                  <TableCell className="text-[12px] text-slate-600">{whName[b.warehouse_id] || "—"}</TableCell>
                  <TableCell>{b.zone || "—"}</TableCell>
                  <TableCell>{b.rack || "—"}</TableCell>
                  <TableCell>{b.shelf || "—"}</TableCell>
                  <TableCell>{b.bin || "—"}</TableCell>
                  <TableCell className="text-right font-mono-num">{b.capacity}</TableCell>
                  <TableCell><button onClick={() => del(b)} className="text-slate-400 hover:text-red-600 p-1"><Trash size={13} /></button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {creating && <BinSheet open={creating} onOpenChange={setCreating} warehouses={warehouses} defaultWarehouseId={warehouseId} onSaved={() => { setCreating(false); load(); onChange?.(); }} />}
    </div>
  );
}

function BinSheet({ open, onOpenChange, warehouses, defaultWarehouseId, onSaved }) {
  const [form, setForm] = useState({ warehouse_id: defaultWarehouseId || "", code: "", zone: "", rack: "", shelf: "", bin: "", capacity: 0, notes: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm((f) => ({ ...f, warehouse_id: defaultWarehouseId || "" })); }, [defaultWarehouseId]);
  const save = async () => {
    if (!form.warehouse_id || !form.code) return toast.error("Warehouse and code are required");
    setSaving(true);
    try {
      await api.post("/materials/bins", { ...form, capacity: Number(form.capacity) });
      toast.success("Bin created");
      onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[420px] max-w-sm">
        <SheetHeader><SheetTitle className="font-display">New Bin Location</SheetTitle></SheetHeader>
        <div className="mt-5 space-y-3">
          <Field label="Warehouse *">
            <Select value={form.warehouse_id} onValueChange={(v) => setForm({ ...form, warehouse_id: v })}>
              <SelectTrigger data-testid="bin-wh"><SelectValue /></SelectTrigger>
              <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Code *"><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="font-mono uppercase" placeholder="A-01-03-B" data-testid="bin-code" /></Field>
          <div className="grid grid-cols-4 gap-2">
            <Field label="Zone"><Input value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} /></Field>
            <Field label="Rack"><Input value={form.rack} onChange={(e) => setForm({ ...form, rack: e.target.value })} /></Field>
            <Field label="Shelf"><Input value={form.shelf} onChange={(e) => setForm({ ...form, shelf: e.target.value })} /></Field>
            <Field label="Bin"><Input value={form.bin} onChange={(e) => setForm({ ...form, bin: e.target.value })} /></Field>
          </div>
          <Field label="Capacity"><Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} /></Field>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} disabled={saving} className="flex-1 rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="bin-save">{saving ? "Saving…" : "Create Bin"}</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// =============================================================================
// Panel: QR / Barcode
// =============================================================================
function QRPanel() {
  const [form, setForm] = useState({ sku: "", warehouse_id: "", batch_number: "" });
  const [qr, setQR] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [scanText, setScanText] = useState("");
  const [scanResult, setScanResult] = useState(null);

  useEffect(() => { api.get("/warehouses").then((r) => setWarehouses(r.data || [])); }, []);

  const generate = async () => {
    if (!form.sku) return toast.error("SKU is required");
    try {
      const p = new URLSearchParams({ sku: form.sku });
      if (form.warehouse_id) p.set("warehouse_id", form.warehouse_id);
      if (form.batch_number) p.set("batch_number", form.batch_number);
      const { data } = await api.get(`/materials/qrcode-datauri/${encodeURIComponent(form.sku)}?${p.toString()}`);
      setQR(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const scan = async () => {
    if (!scanText.trim()) return toast.error("Paste a QR JSON payload");
    try {
      const payload = JSON.parse(scanText);
      const { data } = await api.post("/materials/scan", { payload });
      setScanResult(data);
    } catch (e) {
      if (e instanceof SyntaxError) return toast.error("Invalid JSON — paste the raw QR content");
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Generator */}
      <div className="border border-slate-200 rounded-sm bg-white p-4">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 mb-3 flex items-center gap-2">
          <QrCode size={12} className="text-[#EA580C]" /> Generate QR
        </div>
        <div className="space-y-3">
          <Field label="SKU *"><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="font-mono uppercase" data-testid="qr-sku" /></Field>
          <Field label="Warehouse (optional)">
            <Select value={form.warehouse_id || "__none__"} onValueChange={(v) => setForm({ ...form, warehouse_id: v === "__none__" ? "" : v })}>
              <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Batch # (optional)"><Input value={form.batch_number} onChange={(e) => setForm({ ...form, batch_number: e.target.value })} className="font-mono uppercase" /></Field>
          <Button onClick={generate} className="w-full rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="qr-generate">Generate</Button>
          {qr && (
            <div className="border border-slate-200 rounded-sm p-4 text-center bg-slate-50">
              <img src={qr.data_uri} alt="QR" className="mx-auto" style={{ width: 200, height: 200 }} data-testid="qr-image" />
              <div className="text-[11px] font-mono text-slate-600 mt-2 break-all">{JSON.stringify(qr.payload)}</div>
              <div className="flex gap-2 mt-3 justify-center">
                <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => window.print()}>Print</Button>
                <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => { const a = document.createElement("a"); a.href = qr.data_uri; a.download = `qr-${form.sku}.png`; a.click(); }}>Download</Button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Scanner (paste-based) */}
      <div className="border border-slate-200 rounded-sm bg-white p-4">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 mb-3 flex items-center gap-2">
          <MagnifyingGlass size={12} className="text-[#EA580C]" /> Resolve scanned payload
        </div>
        <div className="space-y-3">
          <Field label="Paste QR content (JSON)"><Textarea rows={5} value={scanText} onChange={(e) => setScanText(e.target.value)} className="font-mono text-[11.5px]" placeholder='{"sku":"CP-22"}' data-testid="qr-scan-input" /></Field>
          <Button onClick={scan} className="w-full rounded-sm bg-[#0F172A] hover:bg-black text-white" data-testid="qr-scan">Resolve</Button>
          {scanResult && (
            <div className="border border-slate-200 rounded-sm p-3 bg-slate-50 text-[12.5px]">
              <div className="label-caps text-slate-500 mb-1">Scope: {scanResult.scope}</div>
              <div className="font-semibold text-slate-900">{scanResult.row?.name}</div>
              <div className="grid grid-cols-2 gap-1 mt-2 text-[11.5px]">
                <div>SKU: <span className="font-mono">{scanResult.row?.sku}</span></div>
                <div>Stock: <span className="font-mono-num font-semibold">{scanResult.row?.stock}</span></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Shared field wrapper
// =============================================================================
function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
